import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const token = searchParams.get('token');
  
  // Password validation states
  const [validations, setValidations] = useState({
    minLength: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSymbol: false,
    passwordsMatch: false
  });

  useEffect(() => {
    if (!token) {
      toast.error(t('reset_toast_invalid_token'));
      navigate('/login');
    }
  }, [token, navigate]);
  
  // Validate password in real-time
  useEffect(() => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const passwordsMatch = password === confirmPassword && password.length > 0;
    
    setValidations({
      minLength,
      hasUpperCase,
      hasNumber,
      hasSymbol,
      passwordsMatch
    });
  }, [password, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('change_password_mismatch'));
      return;
    }

    // Validate password requirements
    if (!validations.minLength || !validations.hasUpperCase || !validations.hasNumber || !validations.hasSymbol) {
      toast.error(t('reset_toast_password_requirements'));
      return;
    }

    setLoading(true);

    try {
      // Use XMLHttpRequest to avoid fetch interceptor issues
      // Backend expects FormData, not JSON
      const formData = new FormData();
      formData.append('token', token);
      formData.append('new_password', password);
      
      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/auth/client/reset-password`);
        
        xhr.onload = () => {
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            json: async () => JSON.parse(xhr.responseText)
          });
        };
        
        xhr.onerror = () => reject(new Error(t('reset_network_error')));
        xhr.send(formData);
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle array of errors or single error
        let errorMessage = t('reset_toast_error');
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            errorMessage = data.detail.map(err => err.msg || err).join(', ');
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          }
        }
        throw new Error(errorMessage);
      }

      toast.success(t('reset_toast_success'));
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = error.message || t('reset_toast_error');
      toast.error(typeof errorMessage === 'string' ? errorMessage : t('reset_toast_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{
      background: 'linear-gradient(135deg, #e8f9ed 0%, #d4f1e0 50%, #c8eed8 100%)'
    }}>
      <div className="w-full max-w-md mx-auto px-6">
        <Card className="glass-card" style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <CardHeader className="text-center py-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <img 
                src="/brand/autogo-logo.svg" 
                alt={t('app_logo_alt')} 
                className="h-12 w-12"
              />
              <span className="text-4xl font-bold" style={{color: '#0a3042'}}>AutoGo</span>
            </div>
            <CardTitle className="text-2xl font-bold" style={{color: '#0a3042'}}>
              {t('reset_title')}
            </CardTitle>
            <CardDescription>
              {t('reset_description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Password Field */}
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-gray-700 mb-2 block">
                  {t('reset_new_password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('ph_password_dots')}
                    required
                    autoComplete="new-password"
                    className="pl-10 pr-10 py-6 text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              {password.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p className="text-xs font-semibold text-gray-700 mb-2">{t('reset_requirements_title')}</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      {validations.minLength ? 
                        <CheckCircle className="w-4 h-4 text-green-500" /> : 
                        <XCircle className="w-4 h-4 text-gray-400" />
                      }
                      <span className={`text-xs ${validations.minLength ? 'text-green-600' : 'text-gray-600'}`}>
                        {t('reset_req_min_length')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {validations.hasUpperCase ? 
                        <CheckCircle className="w-4 h-4 text-green-500" /> : 
                        <XCircle className="w-4 h-4 text-gray-400" />
                      }
                      <span className={`text-xs ${validations.hasUpperCase ? 'text-green-600' : 'text-gray-600'}`}>
                        {t('reset_req_uppercase')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {validations.hasNumber ? 
                        <CheckCircle className="w-4 h-4 text-green-500" /> : 
                        <XCircle className="w-4 h-4 text-gray-400" />
                      }
                      <span className={`text-xs ${validations.hasNumber ? 'text-green-600' : 'text-gray-600'}`}>
                        {t('reset_req_number')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {validations.hasSymbol ? 
                        <CheckCircle className="w-4 h-4 text-green-500" /> : 
                        <XCircle className="w-4 h-4 text-gray-400" />
                      }
                      <span className={`text-xs ${validations.hasSymbol ? 'text-green-600' : 'text-gray-600'}`}>
                        {t('reset_req_symbol')}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Confirm Password Field */}
              <div>
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 mb-2 block">
                  {t('reset_confirm_password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('ph_password_dots')}
                    required
                    autoComplete="new-password"
                    className={`pl-10 pr-10 py-6 text-base ${
                      confirmPassword.length > 0 && !validations.passwordsMatch ? 'border-red-500' : ''
                    } ${
                      confirmPassword.length > 0 && validations.passwordsMatch ? 'border-green-500' : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPassword.length > 0 && !validations.passwordsMatch && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <XCircle className="w-3 h-3" />
                    {t('change_password_mismatch')}
                  </p>
                )}
                {confirmPassword.length > 0 && validations.passwordsMatch && (
                  <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    {t('reset_passwords_match')}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={loading || !validations.minLength || !validations.hasUpperCase || !validations.hasNumber || !validations.hasSymbol || !validations.passwordsMatch}
                className="w-full py-6 text-base font-semibold text-white"
                style={{backgroundColor: '#36e961'}}
              >
                {loading ? t('reset_resetting') : t('reset_submit')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;